import { useEffect, useMemo, useState } from 'react';
import { useDrop, useDrag } from 'react-dnd';
import { getEmptyImage } from 'react-dnd-html5-backend';

import { StoryBoardTracks } from 'services/storyboard.service';
import { selectClipData } from 'components/app/_helpers/_app.helper';
import { EMPTY_CELL } from 'components/app/editor/_mediator/editor.slice';
import { getLinkData, isLinked } from 'components/app/editor/_mediator/editor-clips/editor-clips-linking.helper';
import { SbPosition } from 'components/app/editor/_mediator/editor-controls.slice';
import { GroupRange, useSbDndContext } from './sb-dnd.provider';

/* external iface */
import { useAppDispatch, useAppSelector } from 'store/store-hooks';
import { setHideTooltipListener } from 'components/app/_mediator/app.slice';
import { useClipsDnd } from 'components/app/editor/_mediator/editor-clips/use-clips-dnd';

const DRAG_TYPE = 'clip';
type IsValid = boolean;
interface DropPayload extends SbPosition {
  isValid: boolean;
  cb: () => void;
}

export const useSbDnd = (track: StoryBoardTracks, cell: Index, clipId: CellClip, endCb: () => void) => {
  const { dndRange, targetRange, setTargetRange, setIsDragging } = useSbDndContext();
  const clipsMap = useAppSelector((state) => state.editor.clipsMap);
  const storyBoard = useAppSelector((state) => state.editor.storyBoard);
  const linkedClips = useAppSelector((state) => state.editor.linkedClips);
  const selectedClips = useAppSelector((state) => state.sb.selectedClips);
  const handleClips = useClipsDnd();
  const dispatch = useAppDispatch();

  const [isOver, setIsOver] = useState(false);

  const isClipValid = () => {
    if (!clipsMap) return false;
    const hoveredCell = storyBoard[track][cell];
    return hoveredCell === EMPTY_CELL ? true : selectClipData(hoveredCell, clipsMap).clipGroupId ? false : true;
  };

  const isGroupValid = (targetRange: GroupRange) => {
    const originRange = dndRange.current?.originRange;
    if (!clipsMap || !originRange) return false;
    return !Array.from(Array(targetRange.to - targetRange.from)).some((_, index) => {
      const cell = targetRange.from + index;
      const clipId = storyBoard[track][cell];
      if (clipId === undefined) return true;
      if (clipId === EMPTY_CELL) return false;
      if (isInRange({ track, cell }, originRange)) return false;
      return true;
    });
  };

  const onDndOver: null | IsValid = useMemo(() => {
    if (targetRange) return isInRange({ track, cell }, targetRange) ? isGroupValid(targetRange) : null;
    return isOver ? isClipValid() : null;
  }, [isOver, targetRange]);

  const [, drop] = useDrop(
    () => ({
      accept: DRAG_TYPE,
      drop: (): DropPayload => ({ track, cell, isValid: onDndOver === true, cb: () => setIsOver(false) }),
    }),
    [cell, onDndOver]
  );

  const [{ isDragging }, drag, preview] = useDrag(
    () => ({
      type: DRAG_TYPE,
      item: { track, cell, onDndOver },
      end: (origin: SbPosition, monitor) => {
        endCb();
        const dropPayload = monitor.getDropResult<DropPayload>();
        if (!origin || !dropPayload || !clipId) return;
        if (dropPayload.isValid === true)
          handleClips(
            { track: origin.track, cell: origin.cell - (dndRange.current?.index ?? 0) },
            { track: dropPayload.track, cell: dropPayload.cell - (dndRange.current?.index ?? 0) },
            dndRange.current?.length ?? 1,
            storyBoard
          );
        dropPayload.cb();
      },
      collect: (monitor) => {
        return { isDragging: monitor.isDragging() };
      },
    }),
    [clipId, cell, storyBoard]
  );

  const getDndRange = () => {
    if (!clipId || !clipsMap) return null;
    if (selectedClips.length && selectedClips.includes(clipId)) {
      const groupDraggingCell = selectedClips.findIndex((_clipId) => _clipId === clipId);
      return {
        index: groupDraggingCell,
        clips: selectedClips,
        length: selectedClips.length,
        originRange: getTargetRange({ track, cell }, groupDraggingCell, selectedClips.length),
      };
    }
    const clip = selectClipData(clipId, clipsMap);
    const linkId = clip.clipGroupId;
    if (!isLinked(linkId)) return null;
    else {
      const data = getLinkData(clip, linkedClips);
      return data
        ? {
            index: data.index,
            clips: data.group,
            length: data.group.length,
            originRange: getTargetRange({ track, cell }, data.index, data.group.length),
          }
        : null;
    }
  };

  const onDragEnter = () => {
    if (dndRange.current)
      setTargetRange(getTargetRange({ track, cell }, dndRange.current.index, dndRange.current.length));
    else setIsOver(true);
  };

  const onDragLeave = () => {
    setIsOver(false);
  };

  const onDragStart = () => {
    dispatch(setHideTooltipListener());
    dndRange.current = getDndRange();
  };

  useEffect(() => {
    preview(getEmptyImage(), { captureDraggingState: true });
  }, []);

  useEffect(() => {
    setIsDragging(isDragging);
    if (isDragging) onDragStart();
    else {
      setTargetRange(null);
      dndRange.current = null;
    }
  }, [isDragging]);

  return { drag, drop, onDragEnter, onDragLeave, onDndOver };
};

/* H E L P E R S */

const getTargetRange = (target: SbPosition, groupIndex: Index, groupLen: number) => {
  const from = target.cell - groupIndex;
  const to = from + groupLen;
  return { track: target.track, from, to };
};

const isInRange = (position: SbPosition, range: GroupRange) => {
  return position.track === range.track && position.cell >= range.from && position.cell < range.to;
};
